import { HTMLAttributes } from "react";

/**
 * Icon component for FontAwesome icons matching AngularJS usage
 *
 * This is a simple wrapper for FontAwesome icon classes.
 * In Angular, icons are used with <i class="fa fa-icon-name"></i>
 *
 * Usage:
 *   <UiIcon name="plus" />
 *   <UiIcon name="edit" className="text-blue-500" />
 *   <UiIcon name="trash" size="lg" />
 *
 * NO CHANGES to logic, behavior, or structure - only visual parity.
 */

export interface UiIconProps extends Omit<
  HTMLAttributes<HTMLElement>,
  "children"
> {
  /** FontAwesome icon name (without 'fa-' prefix) */
  name: string;
  /** Icon size: sm, lg, 2x, 3x, etc. */
  size?: "sm" | "lg" | "2x" | "3x" | "4x" | "5x";
  /** Fixed width for icon alignment */
  fixedWidth?: boolean;
  /** Spin the icon */
  spin?: boolean;
  /** Additional CSS classes */
  className?: string;
}

export const UiIcon = ({
  name,
  size,
  fixedWidth = false,
  spin = false,
  className = "",
  ...props
}: UiIconProps) => {
  const classes = [
    "fa", // FontAwesome base class
    `fa-${name}`, // Icon name
    size && `fa-${size}`, // Size modifier
    fixedWidth && "fa-fw", // Fixed width
    spin && "fa-spin", // Spinning animation
    className,
  ]
    .filter(Boolean)
    .join(" ");

  return <i className={classes} aria-hidden="true" {...props} />;
};
